import React, {useState} from 'react';
import {TscPopoverOrigin, TscButtonType, Glyphs, ControlButton} from '@techsmith/tsc-cloud-style-guide';
import ConversationActionMenu from './ConversationActionMenu';
import {IConversationData} from '../../../model/conversationModel';
import {useTranslation} from 'react-i18next';
import ShareLinkModal from '../../modals/ShareLinkModal';
import CreateConversationModal from '../../modals/CreateConversationModal';
import ClearConversationModal from '../../modals/ClearConversationModal';
import config from '../../../service/config';
import TestablePopoverButton from '../../util/TestablePopoverButton';
import Constants from '../../../constants/Constants';
import DataTableActionButtonGroup from '../DataTableActionButtonGroup';
import cssConstants from '../../../constants/cssConstants';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {mediaStore} from '../../../context/mediaProvider';

export const ConversationActionsButtonGroupBase: React.FC<IConversationActionsButtonGroupProps & IStateMappedProps> = ({conversation, onDownloadError, Hash}) => {
   const {t} = useTranslation();
   const [showDeleteConversationModal, setShowDeleteConversationModal] = useState(false);
   const [showCopyConversationLinkModal, setShowCopyConversationLinkModal] = useState(false);
   const [showRenameConversationModal, setShowRenameConversationModal] = useState(false);
   const [showClearCommentsModal, setShowClearCommentsModal] = useState(false);

   return (
      <>
         <DataTableActionButtonGroup
            primaryButton={(
               <ControlButton
                  onClick={() => setShowCopyConversationLinkModal(true)}
                  buttonType={TscButtonType.primary}
                  label={t('conversations.actions.getLink')}
                  title={t('conversations.actions.getLink')}
                  testId="share-conversation-button"
                  glyph={<Glyphs.Share16x16 />}
               />
            )}
            otherButtons={[
               <TestablePopoverButton
                  origin={TscPopoverOrigin.top}
                  buttonType={TscButtonType.secondary}
                  buttonGlyph={<Glyphs.EllipsisStacked16x16 />}
                  width="2rem"
                  closeMenuOnClick={true}
                  buttonTitle={t('conversations.optionsMenu')}
                  testId="more-conversation-options-button"
                  key="more-conversation-options-button"
                  useMobileMenu={true}
                  mobileContextMenuZindex={cssConstants.zIndex.mobileContextMenu}
                  mobileContextMenuTitle={t('conversations.optionsMenu')}
               >
                  <ConversationActionMenu
                     conversation={conversation}
                     setError={onDownloadError}
                     setShowClearCommentsModal={setShowClearCommentsModal}
                  />
               </TestablePopoverButton>,
               <ControlButton
                  onClick={() => setShowDeleteConversationModal(true)}
                  buttonType={TscButtonType.secondary}
                  glyph={<Glyphs.Delete16x16 />}
                  title={t('conversations.actions.delete')}
                  testId="delete-conversation-button"
                  key="delete-conversation-button"
               />,
               <ControlButton
                  onClick={() => setShowRenameConversationModal(true)}
                  buttonType={TscButtonType.secondary}
                  glyph={<Glyphs.Edit16x16 />}
                  title={t('conversations.actions.rename')}
                  testId="rename-conversation-button"
                  key="rename-conversation-button"
               />
            ]}
         />
         {showCopyConversationLinkModal && (
            <ShareLinkModal
               onClose={() => setShowCopyConversationLinkModal(false)}
               title={t('conversations.conversationLink')}
               text={t('conversations.getLinkText')}
               link={`${config.environmentData.PresentationRootUrl}/${Hash}?${Constants.queryParams.conversation}=${conversation.VideoReviewId}`}
            />
         )}
         {showRenameConversationModal && (
            <CreateConversationModal
               conversation={conversation}
               onClose={() => setShowRenameConversationModal(false)}
            />
         )}
         {showDeleteConversationModal && (
            <ClearConversationModal
               conversationHash={conversation.ConversationHash}
               isDeleting={true}
               onClose={() => setShowDeleteConversationModal(false)}
            />
         )}
         {showClearCommentsModal && (
            <ClearConversationModal
               conversationHash={conversation.ConversationHash}
               isDeleting={false}
               onClose={() => setShowClearCommentsModal(false)}
            />
         )}
      </>
   );
};

export interface IConversationActionsButtonGroupProps {
   conversation: IConversationData;
   onDownloadError: () => void;
}

export interface IStateMappedProps {
   Hash: string;
}

const mapStatesToProps = (
   {conversation, onDownloadError}: IConversationActionsButtonGroupProps,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps & IConversationActionsButtonGroupProps => ({conversation, onDownloadError, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(ConversationActionsButtonGroupBase);
